import { useEffect, useState, useMemo } from 'react'
import { notificationService, Notification, NotificationType } from '@/services/notificationService'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import DataTable from '@/components/ui/DataTable'
import FilterBar, { booleanFilter } from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  const loadNotifications = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await notificationService.getAll()
      if (response.success) {
        setNotifications(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des notifications')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTypeLabel = (type: NotificationType) => {
    switch (type) {
      case 'reservation_confirmed': return 'Réservation confirmée'
      case 'payment_received': return 'Paiement reçu'
      case 'new_basket_nearby': return 'Nouveau panier à proximité'
      case 'review_requested': return 'Avis demandé'
      case 'payout_processed': return 'Paiement traité'
      case 'favorite_product_available': return 'Produit favori disponible'
      case 'system_alert': return 'Alerte système'
      default: return type
    }
  }

  const getTypeBadgeVariant = (type: NotificationType) => {
    switch (type) {
      case 'reservation_confirmed': return 'default'
      case 'payment_received': return 'secondary'
      case 'new_basket_nearby': return 'outline'
      case 'review_requested': return 'default'
      case 'payout_processed': return 'secondary'
      case 'favorite_product_available': return 'outline'
      case 'system_alert': return 'destructive'
      default: return 'default'
    }
  }

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id)
      setNotifications(prev => prev.map(n => 
        n.notification_id === id ? { ...n, is_read: true } : n
      ))
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    try {
      for (const notification of notifications.filter(n => !n.is_read)) {
        await markAsRead(notification.notification_id)
      }
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err)
    }
  }

  const columns = useMemo(() => [
    {
      key: 'user',
      label: 'Utilisateur',
      sortable: true,
      render: (row: Notification) => (
        <div className="font-medium">
          {row.user_first_name && row.user_last_name
            ? `${row.user_first_name} ${row.user_last_name}`
            : `Utilisateur ${row.user_id.substring(0, 8)}...`}
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (value: NotificationType) => (
        <Badge variant={getTypeBadgeVariant(value)}>
          {getTypeLabel(value)}
        </Badge>
      )
    },
    {
      key: 'title',
      label: 'Titre',
      sortable: true,
      render: (value: string) => (
        <div className="font-medium">{value}</div>
      )
    },
    {
      key: 'message',
      label: 'Message',
      sortable: false,
      render: (value: string) => (
        <div className="max-w-xs truncate">{value}</div>
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => formatDate(value)
    },
    {
      key: 'is_read',
      label: 'Statut',
      sortable: true,
      render: (value: boolean) => (
        <Badge variant={value ? 'secondary' : 'default'}>
          {value ? 'Lu' : 'Non lu'}
        </Badge>
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      key: 'markAsRead',
      label: 'Marquer comme lu',
      icon: 'Check',
      variant: 'outline' as const,
      size: 'sm' as const,
      show: (row: Notification) => !row.is_read,
      onClick: (row: Notification) => markAsRead(row.notification_id)
    }
  ], [])

  const filterOptions = useMemo(() => [
    {
      key: 'type',
      label: 'Type',
      type: 'select' as const,
      options: [
        { value: 'reservation_confirmed', label: 'Réservation confirmée' },
        { value: 'payment_received', label: 'Paiement reçu' },
        { value: 'new_basket_nearby', label: 'Nouveau panier à proximité' },
        { value: 'review_requested', label: 'Avis demandé' },
        { value: 'payout_processed', label: 'Paiement traité' },
        { value: 'favorite_product_available', label: 'Produit favori disponible' },
        { value: 'system_alert', label: 'Alerte système' }
      ]
    },
    booleanFilter('is_read', 'Statut de lecture'),
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Rechercher par titre ou message...'
    }
  ], [])

  const filteredData = useMemo(() => {
    let result = [...notifications]

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(notification => 
        notification.title.toLowerCase().includes(query) ||
        notification.message.toLowerCase().includes(query) ||
        (notification.user_first_name && notification.user_first_name.toLowerCase().includes(query)) ||
        (notification.user_last_name && notification.user_last_name.toLowerCase().includes(query))
      )
    }

    if (activeFilters.length > 0) {
      activeFilters.forEach(filter => {
        if (filter.key === 'type' && filter.value) {
          result = result.filter(notification => notification.type === filter.value)
        }
        if (filter.key === 'is_read' && filter.value !== undefined) {
          result = result.filter(notification => notification.is_read === filter.value)
        }
      })
    }

    return result
  }, [notifications, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const stats = useMemo(() => ({
    total: notifications.length,
    unread: notifications.filter(n => !n.is_read).length,
    read: notifications.filter(n => n.is_read).length,
    uniqueTypes: new Set(notifications.map(n => n.type)).size,
    byType: notifications.reduce((acc, notification) => {
      acc[notification.type] = (acc[notification.type] || 0) + 1
      return acc
    }, {} as Record<string, number>)
  }), [notifications])

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleFilterChange = (filters: any[]) => {
    setActiveFilters(filters)
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleRefresh = () => {
    loadNotifications()
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Notifications"
        subtitle="Gérez les notifications des utilisateurs"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Notifications', href: '/notifications' }
        ]}
        actions={[
          {
            label: 'Tout marquer comme lu',
            variant: 'outline',
            onClick: markAllAsRead,
            disabled: stats.unread === 0
          },
          {
            label: loading ? 'Chargement...' : 'Actualiser',
            onClick: handleRefresh,
            disabled: loading
          }
        ]}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Total notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Non lues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.unread}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Lues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.read}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Types différents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueTypes}</div>
          </CardContent>
        </Card>
      </div>

      <FilterBar
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        searchPlaceholder="Rechercher par titre, message ou utilisateur..."
      />

      <DataTable
        data={paginatedData}
        columns={columns}
        actions={actions}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: filteredData.length,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
        emptyMessage="Aucune notification trouvée"
      />

      <Card>
        <CardHeader>
          <CardTitle>Répartition par type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.byType).map(([type, count]) => {
              const unreadCount = notifications.filter(n => n.type === type && !n.is_read).length
              const percentage = notifications.length > 0 
                ? Math.round((count / notifications.length) * 100) 
                : 0
              return (
                <div key={type}>
                  <div className="flex justify-between mb-1">
                    <div>
                      <span className="font-medium">{getTypeLabel(type as NotificationType)}</span>
                      <span className="ml-2 text-sm text-muted-foreground">
                        ({unreadCount} non lues)
                      </span>
                    </div>
                    <span className="text-sm font-medium">{count} ({percentage}%)</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default NotificationsPage
